<template>
  <div v-if="recommendation && recommendation.mode" class="reco-block" :class="[variantClass, { 'is-open': open }]">
    <div ref="body" class="reco-body">
      <p class="reco-title">
        <i class="fas fa-info-circle" aria-hidden="true"></i>{{ recommendation.explanation || fallbackTitle }}
      </p>
      <p v-if="evidence" class="reco-evidence">{{ evidence }}</p>
      <span v-if="showDivergenceBadge" class="reco-badge">{{ divergenceText }}</span>
      <p v-if="showOverrideCaution" class="reco-caution">⚠ {{ overrideCautionText }}</p>
    </div>
    <button v-if="clamped" type="button" class="reco-more" @click="open = !open">
      {{ open ? 'פחות' : 'עוד' }}
      <i class="fas" :class="open ? 'fa-chevron-up' : 'fa-chevron-down'" aria-hidden="true"></i>
    </button>
  </div>
</template>

<script>
import { MODE_RANK, MODE_LABELS } from '@/store/modules/cvTextArtifacts'

// Recommendation explanation block for SendCvModeSelectionModal (UI_SPEC C1).
//
// Tertiary by design: it explains, it does not persuade. The only saturated
// colour in the modal stays on the `מומלץ` badge and the selection border.
//
// Bidi: every server-built string is rendered with {{ }} and isolated with CSS
// (`unicode-bidi: plaintext`). Raw-HTML rendering directives are forbidden here,
// as is a literal <bdi> inside server text.
export default {
  name: 'CvSendPatternExplanation',
  props: {
    recommendation: {
      type: Object,
      default: null
    },
    selectedMode: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      open: false,
      clamped: false
    }
  },
  computed: {
    variant () {
      const reco = this.recommendation || {}
      if (reco.downgraded_for_discretion) return 'discretion'
      if (reco.source === 'manual') return 'manual'
      // D2. A learned pattern too weak to follow comes back as source
      // 'default' — the server recommends the company default in that case —
      // so `low_confidence` is the ONLY thing separating it from "this company
      // has no history at all", and it has to be read before the no-history
      // branch or the block renders quiet and unexplained.
      if (reco.low_confidence) return 'low-confidence'
      if (reco.source === 'default' || reco.source === 'no_history') return 'no-history'
      // Defensive: a payload from before the flag existed.
      if (typeof reco.confidence === 'number' && reco.confidence < 0.7) return 'low-confidence'
      return 'learned'
    },
    variantClass () {
      const base = `is-${this.variant}`
      if (this.variant === 'learned' && this.recommendedIsSelected) return `${base} is-selected`
      return base
    },
    recommendedIsSelected () {
      return this.selectedMode === (this.recommendation || {}).mode
    },
    modeLabel () {
      return MODE_LABELS[(this.recommendation || {}).mode] || (this.recommendation || {}).mode
    },
    fallbackTitle () {
      return `שיטת השליחה של החברה: ${this.modeLabel}`
    },
    evidence () {
      // `no-history` is a single line by spec; `manual` shows the title only.
      if (this.variant === 'no-history' || this.variant === 'manual') return ''
      return (this.recommendation || {}).evidence || ''
    },
    showDivergenceBadge () {
      // Never on low-confidence (no badge by spec) and never when she agrees.
      if (this.variant !== 'learned' && this.variant !== 'manual') return false
      return !this.recommendedIsSelected && !!this.selectedMode
    },
    divergenceText () {
      return `לפי היסטוריית השליחה, בדרך כלל נשלח: ${this.modeLabel}`
    },
    showOverrideCaution () {
      if (this.variant !== 'discretion' || !this.selectedMode) return false
      const recoRank = MODE_RANK[(this.recommendation || {}).mode]
      const selRank = MODE_RANK[this.selectedMode]
      if (recoRank === undefined || selRank === undefined) return false
      return selRank < recoRank
    },
    overrideCautionText () {
      return `שליחה במצב ${MODE_LABELS[this.selectedMode] || this.selectedMode} תחשוף שם ומקום עבודה של מועמד/ת דיסקרטי/ת.`
    }
  },
  watch: {
    recommendation () {
      this.$nextTick(this.measureClamp)
    },
    selectedMode () {
      this.$nextTick(this.measureClamp)
    }
  },
  mounted () {
    this.$nextTick(this.measureClamp)
  },
  methods: {
    // The expander is rendered only when the 3-line clamp actually hides text.
    measureClamp () {
      const el = this.$refs.body
      if (!el) {
        this.clamped = false
        return
      }
      this.clamped = el.scrollHeight > el.clientHeight
    }
  }
}
</script>

<style scoped>
.reco-block {
  flex: 0 0 auto;
  margin: 0;
  padding: 12px 14px;
  border: 1px solid #e9ecef;
  border-right: 3px solid #f39c12;
  border-radius: 8px;
  background: #ffffff;
  font-family: inherit;
}
.reco-block.is-selected {
  border-right-color: #3498db;
}
/* Issue 1: #6c757d fails AA on #f8f9fa, so the quiet variants keep a white
   background and let the grey leading bar carry the quiet signal. */
.reco-block.is-no-history {
  border-right-color: #ced4da;
}
.reco-block.is-no-history .reco-title {
  color: #6c757d;
  font-weight: 500;
}
.reco-block.is-low-confidence {
  border-right-color: #adb5bd;
}
.reco-block.is-discretion {
  border: 1px solid #f39c12;
  border-right: 4px solid #f39c12;
  background: #fef9e7;
}
.reco-block.is-discretion .reco-title,
.reco-block.is-discretion .reco-evidence {
  color: #856404;
}

.reco-body {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.reco-block.is-open .reco-body {
  display: block;
  max-height: 120px;
  overflow-y: auto;
  -webkit-line-clamp: none;
}

.reco-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
  color: #2c3e50;
}
.reco-title i {
  font-size: 13px;
  color: #f39c12;
  margin-left: 6px;
}
.reco-block.is-no-history .reco-title i {
  color: #ced4da;
}
.reco-block.is-low-confidence .reco-title i {
  color: #adb5bd;
}
.reco-block.is-discretion .reco-title i {
  color: #f39c12;
}
.reco-evidence {
  margin: 4px 0 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
  color: #6c757d;
}
.reco-badge {
  display: inline-block;
  margin-top: 8px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: #e9ecef;
  color: #495057;
}
.reco-caution {
  margin-top: 6px;
  margin-bottom: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #856404;
}
/* Server strings: CSS bidi isolation, never a literal <bdi>. */
.reco-title,
.reco-evidence,
.reco-caution {
  unicode-bidi: plaintext;
  text-align: right;
}
.reco-more {
  background: none;
  border: 0;
  padding: 0 2px;
  margin-top: 4px;
  border-radius: 2px;
  font-family: inherit;
  font-size: 13px;
  color: #3273dc;
  cursor: pointer;
}
.reco-more:hover {
  text-decoration: underline;
}
.reco-more:focus-visible {
  outline: 2px solid #3273dc;
  outline-offset: 2px;
}
.reco-more i {
  font-size: 10px;
}
</style>
